import { UTCDate } from '@date-fns/utc';
import { Column } from '@tanstack/react-table';
import { addDays, endOfMonth, format, getTime, startOfMonth, subDays, subMonths } from 'date-fns';
import { ChevronDown } from 'lucide-react';
import { useEffect, useState } from 'react';
import { DateRange } from 'react-day-picker';
import { exhaustiveCheck } from '../../utils/exhaustive-check';
import { Button } from '../button/button';
import { Calendar } from '../calendar/calendar';
import { Popover } from '../popover/popover';

type DateRangeFilterProps<TData> = {
  column?: Column<TData>;
};

export const DateRangeFilter = <TData,>({ column }: DateRangeFilterProps<TData>) => {
  const [range, setRange] = useState<DateRange | undefined>();
  const [month, setMonth] = useState<Date | undefined>();

  // Sync local state with column filter value (set from URL params on mount)
  useEffect(() => {
    const filterValue = column?.getFilterValue() as [number, number] | undefined;

    if (!filterValue || !Array.isArray(filterValue) || filterValue.length !== 2) {
      return;
    }

    const [startTimestamp, endTimestamp] = filterValue;
    const startDate = new Date(startTimestamp);
    const endDate = new Date(endTimestamp);

    setRange({ from: startDate, to: endDate });
    setMonth(startDate);
  }, [column]);

  if (
    !column ||
    // Filter should not display if there are no values in the column
    !column.getFacetedUniqueValues().size
  ) {
    return null;
  }

  const triggerText = range?.from && range?.to ? `${format(range.from, 'PPP')} - ${format(range.to, 'PPP')}` : 'Date';

  const onSelectRange = (selectedRange: DateRange | undefined) => {
    setRange(selectedRange);

    if (selectedRange?.from && selectedRange?.to) {
      const fromDate = getTime(selectedRange.from);
      // We add a day so the range includes the to date, otherwise it is set at the start of the day
      const endOfDayToDate = getTime(addDays(selectedRange.to, 1));

      column?.setFilterValue([fromDate, endOfDayToDate]);

      return;
    }

    column?.setFilterValue(undefined);
  };

  const onPresetClick = (preset: 'PREVIOUS_MONTH' | 'CURRENT_MONTH' | 'PREVIOUS_WEEK' | 'CURRENT_WEEK') => {
    const range = getDateRange(preset);

    setMonth(range.from);
    onSelectRange(range);
  };

  return (
    <Popover
      triggerElement={
        <Button
          variant="secondary"
          className="group"
          iconRight={<ChevronDown className="h-4 w-4" />}
          iconRightClassName="transition-transform duration-200 ease-out group-data-[state=open]:rotate-180"
        >
          {triggerText}
        </Button>
      }
      content={
        <div className="flex items-start gap-5 p-5">
          {/* We control the month so we have range presets */}
          <Calendar
            mode="range"
            selected={range}
            onSelect={onSelectRange}
            month={month}
            onMonthChange={setMonth}
            showOutsideDays={false}
          />

          <div className="flex flex-col justify-start gap-3">
            <Button variant="outline" onClick={() => setMonth(new UTCDate())}>
              Go to current month
            </Button>

            <Button variant="outline" onClick={() => onPresetClick('PREVIOUS_WEEK')}>
              Previous week
            </Button>
            <Button variant="outline" onClick={() => onPresetClick('CURRENT_WEEK')}>
              Current week
            </Button>
            <Button variant="outline" onClick={() => onPresetClick('PREVIOUS_MONTH')}>
              Previous month
            </Button>
            <Button variant="outline" onClick={() => onPresetClick('CURRENT_MONTH')}>
              Current month
            </Button>

            <Button onClick={() => onSelectRange(undefined)}>Clear date range</Button>
          </div>
        </div>
      }
      side="bottom"
      align="start"
    />
  );
};

const getDateRange = (preset: 'PREVIOUS_MONTH' | 'CURRENT_MONTH' | 'PREVIOUS_WEEK' | 'CURRENT_WEEK'): DateRange => {
  const today = new UTCDate();
  const currentDay = today.getDay(); // 0 = Sunday, 1 = Monday, etc.

  switch (preset) {
    case 'PREVIOUS_MONTH': {
      const lastMonth = subMonths(new UTCDate(), 1);

      return { from: startOfMonth(lastMonth), to: endOfMonth(lastMonth) };
    }
    case 'CURRENT_MONTH':
      return { from: startOfMonth(today), to: today };
    case 'PREVIOUS_WEEK': {
      const lastSunday = subDays(today, currentDay);
      const lastMonday = subDays(lastSunday, 6);

      return { from: lastMonday, to: lastSunday };
    }
    case 'CURRENT_WEEK': {
      const monday = subDays(today, currentDay === 0 ? 6 : currentDay - 1);

      return { from: monday, to: today };
    }
    default:
      return exhaustiveCheck(preset);
  }
};
